import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Loader2, RefreshCw, Plus, FileDown, Mail, Trash2, Search, AlertTriangle,
  ClipboardCheck, Package, Check, PenTool, ChevronRight,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLivePoll } from "@/hooks/useLivePoll";
import PpfComposeModal from "@/components/PpfComposeModal";
import EmailModal from "@/components/EmailModal";
import DetailDrawer from "@/components/DetailDrawer";
import ReportDocumentView from "@/components/ReportDocumentView";
import { downloadReportPdf } from "@/lib/reportPdf";
import PartsLibrary from "@/components/PartsLibrary";

const ADMIN_ROLES = ["admin", "control", "planner", "superintendent", "supervisor", "owner"];

/**
 * PPFs page — 1:1 with the mobile app's "PPF Management" screen.
 *
 * Layout:
 *   Header (title + New PPF + Refresh)
 *   Tab strip:     [PPF]   [LIBRARY]
 *   Filter chips:  All | Draft | Pending signing | Approved
 *   Search bar
 *   Card list: Asset ID · Description · Author + date · Priority badge · Delete · Status
 */
const PPF_TABS = [
  { id: "ppf", label: "Planning Request", icon: ClipboardCheck },
  { id: "library", label: "Library", icon: Package },
];

const PPF_FILTERS = [
  { id: "all", label: "All" },
  { id: "draft", label: "Draft" },
  { id: "pending", label: "Pending Signing" },
  { id: "approved", label: "Approved" },
];

export default function PPFs() {
  const { user } = useAuth();
  const [tab, setTab] = useState("ppf");
  const [filter, setFilter] = useState("all");
  const [ppfs, setPpfs] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [emailFor, setEmailFor] = useState(null);
  const [detail, setDetail] = useState(null);
  const [autoSignoff, setAutoSignoff] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const reload = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/ppfs");
      const arr = Array.isArray(data) ? data : data?.items || data?.results || data?.data || [];
      setPpfs(arr);
    } catch (ex) {
      setErr(apiError(ex));
      setPpfs([]);
    } finally { setBusy(false); }
  };
  useEffect(() => { reload(); }, []);
  // Auto-refresh every 5s so PPFs created / signed on the mobile app appear here.
  useLivePoll(reload, 5000, tab === "ppf");

  // TOTAL SYNC: keep the open drawer in lockstep with the latest server state.
  // Any mobile-app amendment (parts, WO#, sent-back, sign-off) lands in the
  // drawer within one 5s poll cycle.
  useEffect(() => {
    if (!detail || !Array.isArray(ppfs)) return;
    const id = detail.id || detail._id;
    if (!id) return;
    const latest = ppfs.find((p) => (p.id || p._id) === id);
    if (latest && latest !== detail) {
      setDetail((cur) => ({ ...cur, ...latest }));
    }
  }, [ppfs]);

  // Deep-link support — the Sign-off Inbox lands users here with
  // ?open={id}&signoff=1 so the drawer opens straight into sign-off.
  useEffect(() => {
    if (!ppfs || ppfs.length === 0) return;
    const params = new URLSearchParams(location.search);
    const openId = params.get("open");
    if (!openId) return;
    const match = ppfs.find((p) => (p.id || p._id) === openId);
    if (!match) return;
    setDetail(match);
    setAutoSignoff(params.get("signoff") === "1");
    setTab("ppf");
    // Wipe the query so refresh / back doesn't re-trigger.
    navigate(location.pathname, { replace: true });
  }, [ppfs, location.search, location.pathname, navigate]);

  const canCreate = canAct(user, ADMIN_ROLES);

  const counts = useMemo(() => {
    const c = { all: 0, draft: 0, pending: 0, approved: 0 };
    (ppfs || []).forEach((p) => {
      c.all += 1;
      const s = ppfStatus(p);
      if (c[s] != null) c[s] += 1;
    });
    return c;
  }, [ppfs]);

  const filteredPpfs = useMemo(() => {
    let list = ppfs || [];
    if (filter !== "all") list = list.filter((p) => ppfStatus(p) === filter);
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((p) => {
        const hay = `${p.asset_id || ""} ${p.asset_name || ""} ${p.fault || ""} ${p.description || ""} ${p.created_by_name || ""} ${p.priority || ""}`.toLowerCase();
        return hay.includes(s);
      });
    }
    return list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [ppfs, filter, q]);

  const remove = async (p) => {
    if (!window.confirm(`Delete Planning Request ${p.id || p._id}?`)) return;
    try { await api.delete(`/admin/ppfs/${p.id || p._id}`); reload(); }
    catch (ex) { alert(apiError(ex)); }
  };

  return (
    <div className="space-y-5" data-testid="ppfs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">Planning Requests</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-none mt-1">Planning Requests</h1>
          <div className="text-muted mt-2 text-sm font-mono-tight">
            {tab === "ppf" ? "Planning Requests · Sort · Filter · 1-Tap PDF & Email" : "Parts Library · Search · Verify"}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-ghost" onClick={reload} disabled={busy} data-testid="btn-refresh">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
          {canCreate && tab === "ppf" && (
            <button className="btn btn-sm btn-primary" onClick={() => setShowCreate(true)} data-testid="btn-new">
              <Plus className="w-4 h-4" /> New Planning Request
            </button>
          )}
        </div>
      </div>

      {/* Tab strip — PPF | Library */}
      <div className="flex flex-wrap gap-2" data-testid="ppf-tabs">
        {PPF_TABS.map((t) => {
          const on = t.id === tab;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-black uppercase tracking-wider transition-colors ${
                on ? "text-black" : "text-white/70 hover:text-white"
              }`}
              style={on ? { background: "var(--primary)" } : { background: "var(--surface-2)", border: "1px solid var(--border)" }}
              data-testid={`ppf-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "ppf" ? (
        <>
          {/* Filter chips */}
          <div className="flex flex-wrap gap-2" data-testid="ppf-filters">
            {PPF_FILTERS.map((f) => {
              const on = f.id === filter;
              return (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-black uppercase tracking-widest transition-colors ${
                    on ? "text-black" : "text-white/70 hover:text-white"
                  }`}
                  style={on ? { background: "var(--primary)" } : { background: "var(--surface-2)", border: "1px solid var(--border)" }}
                  data-testid={`ppf-filter-${f.id}`}
                >
                  {f.label}
                  <span className={`tabular-nums text-[10px] ${on ? "text-black/60" : "text-white/50"}`}>{counts[f.id]}</span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="panel-flat px-3 flex items-center gap-2" data-testid="ppf-search-bar">
            <Search className="w-4 h-4 text-muted shrink-0" />
            <input
              className="w-full py-3 bg-transparent outline-none text-base"
              placeholder="Search by asset ID, description, priority…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="ppf-search"
            />
          </div>

          {err && <div className="pill pill-red !text-sm !py-2 !px-3 justify-start"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>}

          {/* Card list */}
          {ppfs === null ? (
            <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading Planning Requests…
            </div>
          ) : filteredPpfs.length === 0 ? (
            <div className="panel-flat p-10 text-center text-muted" data-testid="ppf-empty">
              <ClipboardCheck className="w-8 h-8 mx-auto mb-2 opacity-60" />
              No Planning Requests match this filter.
            </div>
          ) : (
            <div className="grid gap-3" data-testid="ppf-list">
              {filteredPpfs.map((p) => (
                <PpfCard
                  key={p.id || p._id}
                  ppf={p}
                  onOpen={() => setDetail(p)}
                  onPdf={ppfStatus(p) === "approved" ? () => downloadReportPdf(p, "PPF") : null}
                  onEmail={() => setEmailFor(p)}
                  onDelete={canCreate ? () => remove(p) : null}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <PartsLibrary />
      )}

      <PpfComposeModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => { setShowCreate(false); reload(); }}
      />

      {emailFor && (
        <EmailModal open={true} kind="Planning Request" item={emailFor} onClose={() => setEmailFor(null)} />
      )}
      <DetailDrawer
        item={detail}
        onClose={() => { setDetail(null); setAutoSignoff(false); }}
        onUpdated={() => { setDetail(null); setAutoSignoff(false); reload(); }}
        itemLabel={(it) => {
          const asset = it.asset_id || it.asset_name || "Asset";
          const fault = (it.fault || it.description || "").trim();
          const wo = (it.work_order_number || "").trim();
          const parts = [`Planning Request ${asset}`];
          if (fault) parts.push(fault.length > 70 ? fault.slice(0, 68) + "…" : fault);
          if (wo) parts.push(`WO# ${wo}`);
          return parts.join(" — ");
        }}
        resourceName="Planning Request"
        openSignOffOnMount={autoSignoff}
        documentStyle
        extraDetail={detail && <ReportDocumentView type="ppf" item={detail} />}
        signOffConfig={detail && ppfCanSign(detail, user) ? {
          endpoint: (it) => `/ppfs/${it.id || it._id}/complete`,
          label: "Sign & Complete",
          submitLabel: "Sign & Complete Planning Request",
          verb: "complete",
          requireSignature: true,
          noteLabel: "Work Order # (optional)",
          noteType: "input",
          notePlaceholder: "e.g. WO-12345",
          payload: ({ signature, notes }) => ({
            completed_by_sig: signature,
            work_order_number: (notes || "").trim(),
          }),
        } : null}
        sendBackConfig={detail && ppfCanSign(detail, user) ? {
          endpoint: (it) => `/ppfs/${it.id || it._id}/send-back`,
          payload: ({ notes }) => ({ reason: (notes || "").trim() }),
        } : null}
      />
    </div>
  );
}

/* ---------------- Card ---------------- */
function PpfCard({ ppf, onOpen, onPdf, onEmail, onDelete }) {
  const status = ppfStatus(ppf);
  const priority = String(ppf.priority || "medium").toLowerCase();
  const p = PRIORITY[priority] || PRIORITY.medium;
  const s = STATUS_TONE[status] || STATUS_TONE.draft;
  const id = ppf.id || ppf._id;
  const description = ppf.fault || ppf.description || ppf.summary || "Follow-up";
  // Who logged this PPF — prefer the explicit tradesman fields the mobile
  // app now writes; fall back to created_by_name for legacy records.
  const tradesman = ppf.tradesman_name || ppf.created_by_name || ppf.author || ppf.reported_by || "—";
  const trade = ppf.tradesman_trade || "";
  const crew = ppf.crew_name || "";
  const targetSup = ppf.target_supervisor_name || ppf.supervisor_name || "";
  const partsCount = Array.isArray(ppf.parts_needed) ? ppf.parts_needed.length : 0;
  const dateStr = fmt(ppf.created_at);
  const target = ppf.target_completion || ppf.due_at;

  return (
    <div
      className="panel p-4 md:p-5 group hover:border-orange-500/40 transition-all cursor-pointer"
      onClick={onOpen}
      data-testid={`ppf-card-${id}`}
    >
      <div className="flex items-start gap-4">
        {/* Priority badge — left */}
        <div
          className="rounded-xl px-3 py-2 shrink-0 text-center min-w-[64px]"
          style={{ background: p.bg, border: `1px solid ${p.border}`, color: p.color }}
          data-testid={`ppf-priority-${id}`}
        >
          <div className="font-display font-black text-xl leading-none tabular-nums">{p.short}</div>
          <div className="text-[9px] uppercase tracking-widest font-black mt-1">{p.label}</div>
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="pill !text-[10px] !py-0.5 !px-2 font-mono-tight" data-testid={`ppf-asset-${id}`}>
              {ppf.asset_id || ppf.asset_name || "Unassigned"}
            </span>
            {ppf.work_order_number ? (
              <span
                className="pill !text-[10px] !py-0.5 !px-2 uppercase font-black tracking-widest font-mono-tight"
                style={{ background: "rgba(245,130,32,0.12)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.35)" }}
                data-testid={`ppf-wo-${id}`}
                title="Work Order lodged"
              >
                WO# {ppf.work_order_number}
              </span>
            ) : null}
            <span className={`pill !text-[10px] !py-0.5 !px-2 uppercase font-black tracking-widest`}
                  style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color }}
                  data-testid={`ppf-status-${id}`}>
              {s.label}
            </span>
            <span className="text-[10px] font-mono-tight text-muted">#{String(id).slice(0, 8)}</span>
          </div>
          {/* Title — Josh's spec: "PPF {asset} — {fault} — WO#{wo if lodged}".
              Reads exactly the same on the mobile app and the portal. */}
          <div className="font-display font-black text-base md:text-lg mt-2 leading-tight line-clamp-2" data-testid={`ppf-title-${id}`}>
            <span style={{ color: "var(--primary)" }}>Planning Request {ppf.asset_id || ppf.asset_name || "Asset"}</span>
            <span className="text-white/40 mx-1.5">—</span>
            {description}
            {ppf.work_order_number && (
              <>
                <span className="text-white/40 mx-1.5">—</span>
                <span className="font-mono-tight" style={{ color: "var(--primary)" }}>WO# {ppf.work_order_number}</span>
              </>
            )}
          </div>
          {/* Tradesman + trade + crew + assignee — the ONE line every
              planner needs to see at a glance. Josh's complaint was that
              this info was hidden; it's now the second thing on the card. */}
          <div className="flex items-center gap-2 flex-wrap mt-2 text-[11px]" data-testid={`ppf-people-${id}`}>
            <span className="pill !text-[10px] !py-0.5 !px-2" style={{ background: "rgba(139,180,255,0.12)", color: "#8bb4ff", border: "1px solid rgba(139,180,255,0.35)" }}>
              <PenTool className="w-3 h-3" /> {tradesman}{trade ? ` · ${trade}` : ""}
            </span>
            {crew && (
              <span className="pill !text-[10px] !py-0.5 !px-2 opacity-90" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                {crew}
              </span>
            )}
            {targetSup && (
              <span className="pill !text-[10px] !py-0.5 !px-2" style={{ background: "rgba(74,222,128,0.10)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.30)" }}>
                → {targetSup}
              </span>
            )}
            {partsCount > 0 && (
              <span className="pill !text-[10px] !py-0.5 !px-2" style={{ background: "rgba(245,130,32,0.10)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.35)" }}>
                {partsCount} part{partsCount === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted font-mono-tight mt-1 truncate">
            {dateStr}
            {target && <> · Due {fmt(target).split(",")[0]}</>}
            {ppf.smu && <> · SMU {ppf.smu}</>}
            {ppf.estimated_hours && <> · {ppf.estimated_hours}h est.</>}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          {onPdf && (
            <button className="btn btn-sm btn-ghost" onClick={onPdf} title="Download PDF" data-testid={`ppf-pdf-${id}`}>
              <FileDown className="w-4 h-4" />
            </button>
          )}
          <button className="btn btn-sm btn-ghost" onClick={onEmail} title="Email" data-testid={`ppf-email-${id}`}>
            <Mail className="w-4 h-4" />
          </button>
          {status === "pending" && (
            <span className="pill !text-[10px] !py-0.5 !px-2" title="Awaiting signature">
              <PenTool className="w-3 h-3" /> Sign
            </span>
          )}
          {status === "approved" && (
            <span className="pill pill-green !text-[10px] !py-0.5 !px-2" title="Approved">
              <Check className="w-3 h-3" />
            </span>
          )}
          {onDelete && (
            <button className="btn btn-sm btn-ghost text-red-400 hover:text-red-300" onClick={onDelete} title="Delete" data-testid={`ppf-delete-${id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <ChevronRight className="w-4 h-4 text-muted group-hover:text-white transition-colors" />
        </div>
      </div>
    </div>
  );
}

/* ---------------- helpers ---------------- */
const PRIORITY = {
  low:      { short: "P4", label: "LOW",      bg: "#1a2a55", border: "#3b60d1", color: "#8bb4ff" },
  medium:   { short: "P3", label: "MEDIUM",   bg: "#4a2a06", border: "#f58220", color: "#ffb347" },
  high:     { short: "P2", label: "HIGH",     bg: "#5a2f0f", border: "#dc7020", color: "#ffa76a" },
  critical: { short: "P1", label: "CRITICAL", bg: "#5a0f0f", border: "#a51919", color: "#ff9b9b" },
};
const STATUS_TONE = {
  draft:    { label: "DRAFT",   bg: "#141414", border: "#3a3a3a", color: "#a1a1aa" },
  pending:  { label: "PENDING", bg: "#4a2a06", border: "#f58220", color: "#ffb347" },
  approved: { label: "APPROVED", bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" },
};

function ppfStatus(p) {
  const s = String(p?.status || p?.stage || "").toLowerCase();
  if (["approved", "signed", "complete", "closed", "resolved", "done"].some((k) => s.includes(k))) return "approved";
  if (["pending", "submit", "await", "review"].some((k) => s.includes(k))) return "pending";
  return "draft";
}
function ppfCanSign(p, user) {
  if (!p || !user) return false;
  if (ppfStatus(p) !== "pending") return false;
  const r = String(user.role || "").toLowerCase();
  return user.is_admin || ["admin", "control", "owner", "supervisor", "superintendent", "planner"].some((k) => r.includes(k));
}
function canAct(user, roles) {
  if (!user) return false;
  const r = String(user.role || "").toLowerCase();
  return user.is_admin || roles.some((x) => r.includes(x));
}
function fmt(v) { if (!v) return "—"; try { return new Date(v).toLocaleString(); } catch { return String(v); } }
